import type {
	TConversation,
	TAgentDatasetListResponse,
	TAgentDatasetMetaType,
	TMessage,
	TMessageRequest,
	TMessageResponse,
	TPaulInputResponse,
	TPushAgentDatasetPayload,
	TPushAgentDatasetResponse,
	TTurnInfo,
} from "../types/agent";

import { getEndpoint } from "../endpoints";
import { buildPaulInputResponseRequest } from "../utils/paulInput";

import {
	httpServiceInstance,
	type HttpService,
} from "./http.service";

/**
 * Service class for managing agent-related operations.
 */
export class AgentService {
	private static readonly instance: AgentService;
	private static http: HttpService;
	private static endpoint: string;

	/**
	 * Private constructor to prevent instantiation.
	 * Initializes the HttpService instance.
	 */
	constructor() {
		AgentService.http = httpServiceInstance;
		AgentService.endpoint = getEndpoint();
	}

	/**
	 * Retrieves the iframe URL for the agent from the API.
	 * @returns A promise that resolves to the iframe URL response.
	 */
	async getIframeUrl(): Promise<{
		url: string;
	}> {
		const url = `${AgentService.endpoint}/api/agent/iframe`;
		try {
			const { data } = await AgentService.http.get<{
				url: string;
			}>(url);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	}

	public getConversations = async (): Promise<
		Array<TConversation>
	> => {
		const url = `${AgentService.endpoint}/api/agent/conversations`;
		try {
			const { data } =
				await AgentService.http.get<Array<TConversation>>(
					url
				);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public updateConversationTitle = async (
		conversationId: string,
		newTitle: string
	): Promise<TConversation> => {
		const url = `${AgentService.endpoint}/api/agent/conversations/${conversationId}/title`;
		try {
			const { data } =
				await AgentService.http.post<TConversation>(url, {
					data: {
						title: newTitle,
					},
				});
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public getConversationMessagesById = async (
		conversationId: string
	): Promise<TConversation> => {
		const url = `${AgentService.endpoint}/api/agent/conversations/${conversationId}`;
		try {
			const { data } =
				await AgentService.http.get<TConversation>(
					url
				);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public deleteConversation = async (
		conversationId: string
	): Promise<void> => {
		const url = `${AgentService.endpoint}/api/agent/conversations/${conversationId}`;
		try {
			await AgentService.http.delete(url);
		} catch (error: any) {
			throw error.response;
		}
	};

	public getTurnInfo = async (
		conversationId: string,
		turnIdx: number
	): Promise<TTurnInfo> => {
		const url = `${AgentService.endpoint}/api/agent/conversations/${conversationId}/turn_info/${turnIdx}`;
		try {
			const { data } =
				await AgentService.http.get<TTurnInfo>(
					url
				);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public sendMessageToNewConversation = async (
		message: string,
		datasetId: string,
		agentId: string,
		opts: {
			module: TMessageRequest["module"];
			step?: "static" | "perf";
			pointId?: string;
		}
	): Promise<TMessageResponse> => {
		const url = `${AgentService.endpoint}/api/agent/${agentId}/messages`;
		const payload: TMessageRequest = {
			user_query: message,
			dataset_id: datasetId,
			module: opts.module,
			...(opts.step && { step: opts.step }),
			...(opts.module === "caliper" &&
				opts.pointId && { point_id: opts.pointId }),
		};
		try {
			const { data } =
				await AgentService.http.post<TMessageResponse>(
					url,
					{ data: payload }
				);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public sendMessageToConversation = async (
		convId: string,
		message: string,
		agentId: string,
		opts: {
			datasetId?: string;
			module: TMessageRequest["module"];
			step?: "static" | "perf";
			pointId?: string;
		}
	): Promise<TMessageResponse> => {
		const url = `${AgentService.endpoint}/api/agent/${agentId}/messages`;
		try {
			const payload: TMessageRequest = {
				user_query: message,
				conv_id: convId,
				...(opts.datasetId && { dataset_id: opts.datasetId }),
				module: opts.module,
				...(opts.step && { step: opts.step }),
				...(opts.module === "caliper" &&
					opts.pointId && { point_id: opts.pointId }),
			};

			const { data } =
				await AgentService.http.post<TMessageResponse>(
					url,
					{
						data: payload,
					}
				);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public sendInputResponseToConversation = async (
		convId: string,
		inputResponse: TPaulInputResponse,
		agentId: string,
		opts: {
			module: TMessageRequest["module"];
			step?: "static" | "perf";
			pointId?: string;
		}
	): Promise<TMessageResponse> => {
		const url = `${AgentService.endpoint}/api/agent/${agentId}/messages`;
		const payload = buildPaulInputResponseRequest({
			convId,
			module: opts.module,
			step: opts.step,
			response: inputResponse,
		});
		if (opts.module === "caliper" && opts.pointId) {
			payload.point_id = opts.pointId;
		}

		try {
			const { data } =
				await AgentService.http.post<TMessageResponse>(
					url,
					{ data: payload }
				);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};


	public submitFeedback = async (
		convId: string,
		turnIdx: number,
		{
			expectedResult,
			actualResult,
			msgLink,
		}: {
			expectedResult: string;
			actualResult: string;
			msgLink: string;
		}
	): Promise<{ success: boolean }> => {
		const url = `${AgentService.endpoint}/api/agent/conversations/${convId}/turns/${turnIdx}/feedback`;
		try {
			const { data } = await AgentService.http.post<{
				success: boolean;
			}>(url, {
				data: {
					expected_result: expectedResult,
					actual_result: actualResult,
					msg_link: msgLink,
				},
			});
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};


	public rerunMessageCode = async (
		convId: string,
		turnIdx: number,
		code: string
	): Promise<TMessage> => {
		const url = `${AgentService.endpoint}/api/agent/conversations/${convId}/turns/${turnIdx}/rerun`;
		try {
			const { data } =
				await AgentService.http.post<TMessage>(url, {
					data: {
						code,
					},
				});
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public forkConversation = async (
		sourceConvId: string,
		turnIdx: number
	): Promise<{ forked_conv_id: string }> => {
		const url = `${AgentService.endpoint}/api/agent/conversations/${sourceConvId}/fork/${turnIdx}`;
		try {
			const { data } = await AgentService.http.post<{
				forked_conv_id: string;
			}>(url);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public getAgentMeta = async (): Promise<{
		agents: Record<string, {
			id: string;
			name: string;
			description: string;
			endpoint: string;
			datasets: Array<{
				id: string;
				name: string;
				type?: TAgentDatasetMetaType;
				visible: boolean;
			}>;
		}>;
		default_agent_id: string;
		assisted_dataset_id: string;
	}> => {
		const url = `${AgentService.endpoint}/api/agent/meta`;
		try {
			const { data } = await AgentService.http.get<{
				agents: Record<string, {
					id: string;
					name: string;
					description: string;
					endpoint: string;
					datasets: Array<{
						id: string;
						name: string;
						type?: TAgentDatasetMetaType;
						visible: boolean;
					}>;
				}>;
				default_agent_id: string;
				assisted_dataset_id: string;
			}>(url);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public getPaulLlmProvider = async (): Promise<{
		llm_provider: string;
		providers: Array<string>;
	}> => {
		const url = `${AgentService.endpoint}/api/agent/llm_provider`;
		try {
			const { data } = await AgentService.http.get<{
				llm_provider: string;
				providers: Array<string>;
			}>(url);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public setPaulLlmProvider = async (
		llmProvider: string
	): Promise<{
		llm_provider: string;
		providers: Array<string>;
	}> => {
		const url = `${AgentService.endpoint}/api/agent/llm_provider`;
		const config = {
			data: { llm_provider: llmProvider },
		};
		try {
			const { data } = await AgentService.http.put<{
				llm_provider: string;
				providers: Array<string>;
			}>(url, config);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public getAgentDatasets = async (): Promise<
		TAgentDatasetListResponse
	> => {
		const url = `${AgentService.endpoint}/api/agent/datasets`;
		try {
			const { data } =
				await AgentService.http.get<TAgentDatasetListResponse>(
					url
				);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};

	public pushAgentDataset = async (
		payload: TPushAgentDatasetPayload
	): Promise<TPushAgentDatasetResponse> => {
		const url = `${AgentService.endpoint}/api/agent/datasets`;
		try {
			const { data } =
				await AgentService.http.post<TPushAgentDatasetResponse>(
					url,
					{
						data: payload,
					}
				);
			return data;
		} catch (error: any) {
			throw error.response;
		}
	};
}
